// src/pages/enterprise-ai/LeadsPage.js
//
// Inbound Leads: demo requests booked (or attempted) from the public landing page.
// This is DISTINCT from the GTM outbound section — these are prospects who came to us.
//
// Data flow: T5 (this page) -> T6 (admin API) -> T1 (demo_requests store).
//   GET ${T6_BASE}/api/admin/leads          -> { ok, count, limit, nextBefore, demoRequests: [...] }
//   GET ${T6_BASE}/api/admin/leads/stats     -> { ok, total, booked, failed, last7Days, last30Days }
//
// Auth + base URL follow the project's proven T6 admin idiom (EnterpriseAgentAccessTab.js /
// useGtm.js / apiService.js): a Firebase ID token in an Authorization: Bearer header on a
// REACT_APP_BACKEND_URL base. No new state libraries — plain React state + axios, like the app.
//
// Rule 3: loading, empty, and error states are all rendered explicitly; nothing collapses to
// a silent blank. booking_failed rows are surfaced prominently (a prospect hit an error and
// needs manual follow-up).

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Collapse, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { THEME } from './genesisData';

// T6 admin API base. Matches apiService.js / EnterpriseAgentAccessTab.js / useGtm.js.
const T6_BASE = process.env.REACT_APP_BACKEND_URL || 'https://api.mgmt.joinunited.ai';
const PAGE_LIMIT = 50;

// Firebase-bearer header, the exact idiom the other live admin surfaces use.
async function authHeader() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated. Please sign in again.');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// Turn an axios error into a readable message; a 401/403 reads as exactly that (the admin
// token isn't attached) rather than a blank screen.
function readError(err) {
  const status = err && err.response && err.response.status;
  if (status === 401 || status === 403) {
    return `Not authorized (${status}). Sign in with a staff account that has admin access.`;
  }
  return (
    (err && err.response && err.response.data &&
      (err.response.data.message || err.response.data.detail || err.response.data.error)) ||
    (err && err.message) ||
    'Request failed.'
  );
}

// Render an ISO timestamp in the VIEWER's local timezone (date + time). Null-safe.
function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
// Compact created timestamp in the viewer's local timezone, no year: "Jul 31, 1:14 PM".
function fmtCreatedCompact(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// The demo time rendered in the PROSPECT's timezone with its abbreviation, e.g.
// "Aug 4, 8:00 AM PDT". Falls back to the viewer's local zone if tz is missing/invalid.
function fmtDemoTime(iso, tz) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const opts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
  try {
    if (tz) return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: tz }).format(d);
  } catch (e) { /* invalid IANA zone -> fall back to viewer local */ }
  return new Intl.DateTimeFormat(undefined, opts).format(d);
}

const fmtCount = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString('en-US'));

// Status pill: booked = mint (green), booking_failed = amber, anything else = neutral gray.
const StatusBadge = ({ status }) => {
  const tone = status === 'booked'
    ? { bg: THEME.mintDim, color: THEME.mint, label: 'Booked' }
    : status === 'booking_failed'
      ? { bg: THEME.amberDim, color: THEME.amber, label: 'Booking failed' }
      : { bg: 'rgba(255,255,255,0.06)', color: THEME.textSecondary, label: status || 'unknown' };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', background: tone.bg, color: tone.color,
        fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
        px: '8px', py: '3px', borderRadius: '4px', lineHeight: 1, whiteSpace: 'nowrap',
      }}
    >
      {tone.label}
    </Box>
  );
};

// One top-of-page stat card.
const StatCard = ({ label, value, tone, loading }) => (
  <Box sx={{ flex: '1 1 150px', minWidth: 140, border: `1px solid ${THEME.border}`, borderLeft: `3px solid ${tone || THEME.mint}`, borderRadius: '12px', background: THEME.card, p: 2 }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted }}>{label}</Typography>
    {loading
      ? <CircularProgress size={20} sx={{ color: tone || THEME.mint, my: 0.75 }} />
      : <Typography sx={{ fontSize: '26px', fontWeight: 700, color: THEME.textPrimary, lineHeight: 1.2 }}>{value}</Typography>}
  </Box>
);

// The 7-column grid shared by the header and every row (last cell is the expand chevron).
// Name / email / company get flexible width with generous minimums so they render in full
// at typical lengths; the two date columns and status are compact fixed widths.
const GRID = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, 1.5fr) minmax(190px, 2fr) minmax(110px, 1.3fr) 156px 128px 118px 24px',
  gap: 10,
  alignItems: 'center',
};

const HeaderCellSx = { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted };
const CellSx = { fontSize: '13px', color: THEME.textSecondary, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// One labeled field in the expanded detail panel.
const DetailField = ({ label, value, mono }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted, mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, wordBreak: 'break-word', fontFamily: mono ? 'monospace' : undefined }}>
      {value == null || value === '' ? '—' : value}
    </Typography>
  </Box>
);

// One lead row (click to expand). booking_failed rows are visually prominent: an amber left
// rail, a faint amber wash, and a "Needs follow-up" flag, because a prospect hit an error.
const LeadRow = ({ lead, expanded, onToggle }) => {
  const failed = lead.status === 'booking_failed';
  const errorInfo = lead.error || lead.errorMessage || lead.failureReason || lead.bookingError || lead.reason || '';
  return (
    <Box
      sx={{
        border: `1px solid ${failed ? 'rgba(251,191,36,0.35)' : THEME.border}`,
        borderLeft: `3px solid ${failed ? THEME.amber : 'transparent'}`,
        borderRadius: '10px',
        background: failed ? 'rgba(251,191,36,0.06)' : THEME.card,
        mb: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        sx={{ ...GRID, px: 1.5, py: 1, cursor: 'pointer', userSelect: 'none', '&:hover': { background: 'rgba(255,255,255,0.03)' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {failed && <WarningAmberIcon sx={{ fontSize: 15, color: THEME.amber, flexShrink: 0 }} />}
          <Typography title={lead.name || ''} sx={{ ...CellSx, color: THEME.textPrimary, fontWeight: 600 }}>{lead.name || '—'}</Typography>
        </Box>
        {lead.email ? (
          <Typography
            component="a"
            href={`mailto:${lead.email}`}
            title={lead.email}
            onClick={(e) => e.stopPropagation()}
            sx={{ ...CellSx, color: THEME.mint, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {lead.email}
          </Typography>
        ) : (
          <Typography sx={{ ...CellSx, color: THEME.textMuted }}>—</Typography>
        )}
        <Typography title={lead.company || ''} sx={CellSx}>{lead.company || '—'}</Typography>
        <Typography title={fmtDemoTime(lead.requestedStartISO, lead.tz)} sx={CellSx}>{fmtDemoTime(lead.requestedStartISO, lead.tz)}</Typography>
        <Typography title={fmtDateTime(lead.createdAt)} sx={CellSx}>{fmtCreatedCompact(lead.createdAt)}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}><StatusBadge status={lead.status} /></Box>
        <ExpandMoreIcon sx={{ fontSize: 18, color: THEME.textMuted, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: `1px solid ${THEME.border}` }}>
          {failed && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.5, mt: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 16, color: THEME.amber, flexShrink: 0, mt: '1px' }} />
              <Typography sx={{ fontSize: '12px', color: THEME.amber, lineHeight: 1.5 }}>
                This prospect tried to book and hit an error. Needs manual follow-up.{errorInfo ? ` — ${errorInfo}` : ''}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5, mt: 1 }}>
            <DetailField label="Demo time" value={fmtDemoTime(lead.requestedStartISO, lead.tz)} />
            <DetailField label="Requested timezone" value={lead.tz} />
            <DetailField label="Source" value={lead.source} />
            <DetailField label="Created" value={fmtDateTime(lead.createdAt)} />
            <DetailField label="Booking UID" value={lead.bookingUid} mono />
            <DetailField label="Lead ID" value={lead._id} mono />
            {errorInfo ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <DetailField label="Error" value={errorInfo} />
              </Box>
            ) : null}
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: THEME.textMuted, mb: 0.25 }}>User agent</Typography>
              <Typography title={lead.userAgent || ''} sx={{ fontSize: '11px', color: THEME.textFaint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {lead.userAgent || '—'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// A plain inline error block with an optional retry, reused for the stats and list surfaces.
const ErrorBlock = ({ message, onRetry }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', border: '1px solid rgba(198,61,87,0.3)', background: 'rgba(198,61,87,0.08)', borderRadius: '10px', p: 2 }}>
    <WarningAmberIcon sx={{ fontSize: 18, color: THEME.pink }} />
    <Typography sx={{ fontSize: '13px', color: THEME.pink, flex: 1, minWidth: 0 }}>{message}</Typography>
    {onRetry && (
      <Button size="small" onClick={onRetry} startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} sx={{ color: THEME.pink, textTransform: 'none' }}>
        Retry
      </Button>
    )}
  </Box>
);

export default function LeadsPage() {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const [leads, setLeads] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(true);   // first page
  const [loadingMore, setLoadingMore] = useState(false);    // subsequent pages
  const [leadsError, setLeadsError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const headers = await authHeader();
      const res = await axios.get(`${T6_BASE}/api/admin/leads/stats`, { headers });
      setStats(res.data || null);
    } catch (err) {
      setStatsError(readError(err));
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // `before` is the nextBefore cursor from the previous page; absent = first page.
  const loadLeads = useCallback(async (before) => {
    const isFirst = !before;
    if (isFirst) { setLeadsLoading(true); setLeadsError(null); } else { setLoadingMore(true); }
    try {
      const headers = await authHeader();
      const params = before ? { before, limit: PAGE_LIMIT } : { limit: PAGE_LIMIT };
      const res = await axios.get(`${T6_BASE}/api/admin/leads`, { headers, params });
      const body = res.data || {};
      const rows = Array.isArray(body.demoRequests) ? body.demoRequests : (Array.isArray(body) ? body : []);
      setLeads((prev) => (isFirst ? rows : [...prev, ...rows]));
      setNextBefore(body.nextBefore || null);
    } catch (err) {
      setLeadsError(readError(err));
      if (isFirst) setLeads([]);
    } finally {
      if (isFirst) setLeadsLoading(false); else setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadLeads();
  }, [loadStats, loadLeads]);

  const refresh = () => {
    setExpandedId(null);
    setNextBefore(null);
    loadStats();
    loadLeads();
  };

  const statCards = [
    { label: 'Total', value: fmtCount(stats && stats.total), tone: THEME.mint },
    { label: 'Booked', value: fmtCount(stats && stats.booked), tone: THEME.mint },
    { label: 'Booking failed', value: fmtCount(stats && stats.failed), tone: THEME.amber },
    { label: 'Last 7 days', value: fmtCount(stats && stats.last7Days), tone: THEME.blue },
    { label: 'Last 30 days', value: fmtCount(stats && stats.last30Days), tone: THEME.blue },
  ];

  const showEmpty = !leadsLoading && !leadsError && leads.length === 0;

  return (
    <Box sx={{ p: 3, color: THEME.textPrimary, maxWidth: 1200 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: THEME.textPrimary, lineHeight: 1.2 }}>Leads</Typography>
          <Typography sx={{ fontSize: '13px', color: THEME.textSecondary, mt: 0.5, maxWidth: 560, lineHeight: 1.6 }}>
            Inbound demo requests from the public landing page. Booking failures are people who tried to book and hit an error; they need manual follow-up.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={refresh}
          startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          sx={{ color: THEME.mint, borderColor: THEME.border, textTransform: 'none', flexShrink: 0, '&:hover': { borderColor: THEME.mint, background: THEME.mintFaint } }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stat cards */}
      {statsError ? (
        <Box sx={{ mb: 3 }}><ErrorBlock message={`Could not load stats: ${statsError}`} onRetry={loadStats} /></Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          {statCards.map((c) => (
            <StatCard key={c.label} label={c.label} value={c.value} tone={c.tone} loading={statsLoading} />
          ))}
        </Box>
      )}

      {/* List */}
      {leadsLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
          <CircularProgress size={20} sx={{ color: THEME.mint }} />
          <Typography sx={{ fontSize: '13px', color: THEME.textMuted }}>Loading demo requests…</Typography>
        </Box>
      ) : leadsError ? (
        <ErrorBlock message={`Could not load demo requests: ${leadsError}`} onRetry={() => loadLeads()} />
      ) : showEmpty ? (
        <Box sx={{ border: `1px dashed ${THEME.border}`, borderRadius: '12px', p: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '14px', color: THEME.textMuted }}>No demo requests yet</Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 900 }}>
            {/* Column header */}
            <Box sx={{ ...GRID, px: 1.5, py: 0.75 }}>
              <Typography sx={HeaderCellSx}>Name</Typography>
              <Typography sx={HeaderCellSx}>Email</Typography>
              <Typography sx={HeaderCellSx}>Company</Typography>
              <Typography sx={HeaderCellSx}>Demo time</Typography>
              <Typography sx={HeaderCellSx}>Created</Typography>
              <Typography sx={{ ...HeaderCellSx, textAlign: 'right' }}>Status</Typography>
              <Box />
            </Box>
            {leads.map((lead, i) => {
              const id = lead._id || `${lead.email || 'lead'}-${i}`;
              return (
                <LeadRow
                  key={id}
                  lead={lead}
                  expanded={expandedId === id}
                  onToggle={() => setExpandedId((cur) => (cur === id ? null : id))}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* Load more (cursor) + a non-fatal error if a later page fails */}
      {!leadsLoading && !leadsError && nextBefore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => loadLeads(nextBefore)}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} sx={{ color: THEME.mint }} /> : null}
            sx={{ color: THEME.mint, borderColor: THEME.border, textTransform: 'none', '&:hover': { borderColor: THEME.mint, background: THEME.mintFaint } }}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
      {!leadsLoading && leadsError && leads.length > 0 && (
        <Box sx={{ mt: 2 }}><ErrorBlock message={`Could not load more: ${leadsError}`} onRetry={() => loadLeads(nextBefore)} /></Box>
      )}
    </Box>
  );
}
